import { useState, useEffect } from "react";
import { getFAQsAPI } from "../../services/api";
import Glossary from "../ui_elements/transparency_portal/Glossary";
import { MagnifyingGlassIcon } from "@heroicons/react/24/outline";

const FAQ = () => {
  const [faqs, setFaqs] = useState([]);
  const [openIndex, setOpenIndex] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(true);
  const [apiUnavailable, setApiUnavailable] = useState(false);

  useEffect(() => {
    getFAQsAPI()
      .then(({ data }) => setFaqs(data))
      .catch(() => setApiUnavailable(true))
      .finally(() => setLoading(false));
  }, []);

  const toggle = (index) => setOpenIndex(index === openIndex ? null : index);

  const normalized = searchTerm.toLowerCase();
  const visibleFaqs = faqs.filter(
    (item) =>
      item.question.toLowerCase().includes(normalized) ||
      item.answer.toLowerCase().includes(normalized)
  );

  return (
    <div className="p-6 md:p-8 space-y-10 max-w-4xl mx-auto">

      {/* Intro */}
      <div className="bg-blue-50 border border-blue-100 rounded-xl p-6 space-y-2">
        <h2 className="text-xl font-bold text-blue-950">
          O que são as Perguntas e Respostas?
        </h2>
        <p className="text-blue-900 text-sm leading-relaxed">
          Esta seção reúne as dúvidas mais comuns dos cidadãos sobre os serviços,
          dados e funcionamento da Prefeitura de Serrinha. Use o campo de pesquisa
          para encontrar uma pergunta rapidamente, ou navegue pela lista e clique
          em qualquer item para expandir a resposta.
        </p>
        <p className="text-blue-900 text-sm leading-relaxed">
          Não encontrou o que procurava? Entre em contato pelos nossos{" "}
          <a href="/transparencia/canais-de-comunicacao" className="underline font-medium hover:text-blue-700">
            Canais de Comunicação
          </a>.
        </p>
      </div>

      {/* FAQ section */}
      <section id="perguntas-frequentes">
        <h2 className="text-2xl font-bold text-gray-800 mb-4 flex items-center">
          Perguntas Frequentes
          <span className="flex-1 border-t border-gray-300 ml-4" />
        </h2>

        {loading && (
          <p className="text-gray-500 text-center py-8">Carregando perguntas...</p>
        )}

        {!loading && apiUnavailable && (
          <div className="p-5 bg-yellow-50 border border-yellow-200 rounded-xl text-sm text-yellow-800">
            <strong>Em breve:</strong> as perguntas frequentes serão carregadas
            automaticamente. Nossa equipe está trabalhando para disponibilizar
            este conteúdo.
          </div>
        )}

        {!loading && !apiUnavailable && (
          <>
            {/* Search */}
            <div className="relative mb-5">
              <MagnifyingGlassIcon className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400 pointer-events-none" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Pesquisar perguntas..."
                className="w-full border border-gray-300 rounded-xl pl-12 pr-4 py-3 text-base text-gray-800 shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition"
              />
            </div>

            {visibleFaqs.length === 0 && searchTerm && (
              <p className="text-gray-500 text-center py-8">
                Nenhum resultado encontrado para "{searchTerm}".
              </p>
            )}

            <div className="space-y-3">
              {visibleFaqs.map((item, index) => (
                <div
                  key={item.id ?? index}
                  className="border border-gray-200 rounded-xl shadow-sm overflow-hidden"
                >
                  <button
                    onClick={() => toggle(index)}
                    className="w-full text-left px-5 py-4 bg-gray-50 text-gray-800 font-medium flex justify-between items-center hover:bg-gray-100 transition"
                  >
                    <span className="pr-4">{item.question}</span>
                    <span className="text-xl shrink-0 text-blue-700">
                      {openIndex === index ? "−" : "+"}
                    </span>
                  </button>
                  {openIndex === index && (
                    <div className="px-5 py-4 text-gray-700 text-sm leading-relaxed bg-white border-t border-gray-100">
                      {item.answer}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </>
        )}
      </section>

      {/* Glossary */}
      <section id="glossario">
        <h2 className="text-2xl font-bold text-gray-800 mb-4 flex items-center">
          Glossário
          <span className="flex-1 border-t border-gray-300 ml-4" />
        </h2>
        <p className="text-gray-500 text-sm mb-5">
          Consulte os termos técnicos usados nos relatórios e documentos do
          Portal da Transparência.
        </p>
        <div className="p-4 bg-gray-50 rounded-xl shadow-sm">
          <Glossary />
        </div>
      </section>

    </div>
  );
};

export default FAQ;
